import { Col, Divider, Input, Select } from "antd";
import { CheckOutlined, SearchOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import React, { useMemo, useState } from "react";

import "./MultiSelectFilter.scss";

const MultiSelectFilter = ({
  title,
  placeholder,
  listOptions = [],
  value = [],
  onChange,
  width = 240,
  dropdownWidth = 300,
  disabled = false,
  showActions = true,
  multiple = true
}) => {
  const [searchText, setSearchText] = useState("");

  // Filter options based on search text
  const filteredOptions = useMemo(() => {
    if (!searchText) return listOptions;
    return listOptions.filter(
      (option) =>
        option.label?.toLowerCase().includes(searchText.toLowerCase()) ||
        option.value?.toString().toLowerCase().includes(searchText.toLowerCase())
    );
  }, [listOptions, searchText]);

  // Handle select all visible options
  const handleSelectAll = () => {
    const allVisibleValues = filteredOptions.map((opt) => opt.value);
    const newValues = [...new Set([...value, ...allVisibleValues])];
    onChange?.(newValues);
  };

  // Handle clear all selections
  const handleClearAll = () => {
    onChange?.([]);
  };

  // Handle individual option change
  const handleOptionChange = (optionValue, checked) => {
    if (multiple) {
      // Multiple select mode
      if (checked) {
        onChange?.([...value, optionValue]);
      } else {
        onChange?.(value.filter((v) => v !== optionValue));
      }
    } else {
      // Single select mode - toggle or replace
      if (checked) {
        onChange?.([optionValue]);
      } else {
        onChange?.([]);
      }
    }
  };

  // Custom dropdown render
  const dropdownRender = () => (
    <div className="multi-select-filter-dropdown">
      {/* Search input */}
      <div className="dropdown-search">
        <Input
          placeholder="検索..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
      </div>

      <Divider style={{ margin: "8px 0" }} />

      {/* Options list with checkboxes */}
      <div className="dropdown-options">
        {filteredOptions.length === 0 ? (
          <div className="dropdown-empty">データがありません</div>
        ) : (
          filteredOptions.map((option) => {
            const isChecked = value.includes(option.value);
            return (
              <div
                key={option.value}
                className={`dropdown-option ${isChecked ? "selected" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleOptionChange(option.value, !isChecked);
                }}
              >
                <span className={`custom-checkbox ${isChecked ? "checked" : ""}`}>
                  {isChecked && <CheckOutlined className="checkmark" />}
                </span>
                <span className="option-label">{option.label}</span>
              </div>
            );
          })
        )}
      </div>

      {showActions && (
        <>
          <Divider style={{ margin: "8px 0" }} />
          <div className="dropdown-actions">
            <span className="action-btn" onClick={handleClearAll}>
              選択をクリア
            </span>
            <span className="action-btn" onClick={handleSelectAll}>
              全選択
            </span>
          </div>
        </>
      )}
    </div>
  );

  // For single select, convert array value to single value for display
  const displayValue = multiple ? value : (value.length > 0 ? value[0] : undefined);

  return (
    <Col className={`filter-item multi-select-filter ${!multiple ? "single-mode" : ""}`}>
      {title && <div className="mb-5">{title}</div>}
      <Select
        mode={multiple ? "multiple" : undefined}
        style={{ width }}
        placeholder={placeholder}
        value={displayValue}
        onChange={onChange}
        disabled={disabled}
        maxTagCount={multiple ? 1 : undefined}
        maxTagPlaceholder={multiple ? (omittedValues) => `+${omittedValues.length}` : undefined}
        dropdownRender={dropdownRender}
        dropdownStyle={{ padding: 0, minWidth: dropdownWidth }}
        popupMatchSelectWidth={false}
        showSearch={false}
        open={undefined}
        listHeight={0}
        virtual={false}
        popupClassName="multi-select-filter-popup"
        allowClear={!multiple}
        onClear={() => onChange?.([])}
      >
        {listOptions.map((option) => (
          <Select.Option key={option.value} value={option.value}>
            {option.label}
          </Select.Option>
        ))}
      </Select>
    </Col>
  );
};

MultiSelectFilter.propTypes = {
  title: PropTypes.string,
  placeholder: PropTypes.string,
  listOptions: PropTypes.array,
  value: PropTypes.array,
  onChange: PropTypes.func,
  width: PropTypes.number,
  dropdownWidth: PropTypes.number,
  disabled: PropTypes.bool,
  showActions: PropTypes.bool,
  multiple: PropTypes.bool
};

export default MultiSelectFilter;
